import type { TFunction } from 'i18next';
import { safeParseDateInput } from '../../../../helpers/date-time-helpers/parsing/parsing';
import type { MeasureMetadata, WorkPeriod } from '../../work-periods.type';

/**
 * Returns the last non-null, numeric value from a time series data object.
 *
 * The function expects an object where keys are date strings and values are numbers (or null/undefined).
 * It sorts the keys (dates) in ascending order, then iterates from the latest date backwards,
 * returning the first value that is not null, undefined, or NaN.
 *
 * @param {Record<string, number> | undefined | null} timeSeriesData - The time series data object.
 * @returns {number | null} The last valid numeric value, or null if none found.
 */

function getLastValue(timeSeriesData: Record<string, number> | undefined | null): number | null {
  if (!timeSeriesData || typeof timeSeriesData !== 'object') return null;

  try {
    const keys = Object.keys(timeSeriesData);
    if (keys.length === 0) return null;

    // Filter out any non-string keys and sort
    const dates = keys.filter((key) => key != null && typeof key === 'string').sort();
    if (dates.length === 0) return null;

    // Find the last non-null value
    for (let i = dates.length - 1; i >= 0; i--) {
      const date = dates[i];
      if (!date) continue;

      const value = timeSeriesData[date];
      if (value !== null && value !== undefined && !isNaN(Number(value))) {
        return Number(value);
      }
    }

    return null;
  } catch (error) {
    console.warn('Error in getLastValue:', error);
    return null;
  }
}

/**
 * Returns the label for the units of a measure
 * @param {string} units - The units of a measure
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The label for the units of a measure
 */
const getUnitsLabel = (units: string, t: TFunction): string => {
  switch (units) {
    case 'Percentage':
      return t('process_analysis.units.percentage');
    case 'Hours':
      return 'hrs';
    case 'Days':
      return t('process_analysis.units.days').toLowerCase();
    case 'Points':
      return t('process_analysis.units.points', { estimation: t('common.estimation') }).toLowerCase();
    case 'Tasks':
      return t('common.work_units').toLowerCase();
    default:
      return '';
  }
};

/**
 * Safely sorts work periods by start date (oldest to newest) without mutating the original array
 * @param {WorkPeriod[]} workPeriods - The work periods array to sort
 * @returns {WorkPeriod[]} A new array of work periods sorted by start_date
 */
const sortWorkPeriodsByStartDate = (workPeriods: WorkPeriod[]): WorkPeriod[] => {
  return [...workPeriods].sort((a, b) => {
    const aTime =
      a.start_date && safeParseDateInput(a.start_date) ? (safeParseDateInput(a.start_date)?.unix() ?? 0) : 0;
    const bTime =
      b.start_date && safeParseDateInput(b.start_date) ? (safeParseDateInput(b.start_date)?.unix() ?? 0) : 0;
    if (aTime === null || bTime === null) return 0;
    if (aTime < bTime) return -1;
    if (aTime > bTime) return 1;
    if (aTime === bTime) return 0;
    return 0;
  });
};
/**
 * Formats the measure data for Flora
 * Replaces the measure keys with the measure titles
 * @param {Record<string, any>} measureData - The measure data
 * @param {MeasureMetadata[]} measureMetadata - The measure metadata
 * @param {WorkPeriod[]} workPeriods - The work periods
 * @param {string[]} includedMeasures - The included measures
 * @param {boolean} showValuesOverTime - Whether to show values over time
 * @returns {Record<string, any>} The formatted measure data
 */
const formatWorkPeriodDataForFlora = (
  measureData: Record<string, any>,
  measureMetadata: MeasureMetadata[],
  workPeriods: WorkPeriod[],
  includedMeasures?: string[],
  showValuesOverTime = false,
): Record<string, any> => {
  const measureNameToTitle: Record<string, string> = {};
  measureMetadata.forEach((m) => {
    measureNameToTitle[m.measure_name] = m.measure_title;
  });
  const workPeriodIdToName: Record<string, string> = {};
  workPeriods.forEach((w) => {
    workPeriodIdToName[w.id] = w.name;
  });

  const formatted: Record<string, any> = {};

  Object.entries(measureData).forEach(([workPeriodId, measures]) => {
    if (!measures || typeof measures !== 'object') {
      formatted[workPeriodIdToName[workPeriodId]] = measures;
      return;
    }
    const newMeasures: Record<string, any> = {};
    Object.entries(measures).forEach(([measureKey, value]) => {
      // The `phases` companion array rides on the same response as
      // measures but is presentation metadata, not a measure — skip it so
      // it never leaks into Flora prompts.
      if (measureKey === 'phases') return;
      if (includedMeasures && !includedMeasures.includes(measureKey)) return;
      const title = measureNameToTitle[measureKey] || measureKey;
      if (showValuesOverTime) {
        newMeasures[title] = value;
      } else {
        newMeasures[title] = getLastValue(value as Record<string, number> | null | undefined);
      }
    });
    formatted[workPeriodIdToName[workPeriodId]] = newMeasures;
  });

  return formatted;
};

export { formatWorkPeriodDataForFlora, getLastValue, getUnitsLabel, sortWorkPeriodsByStartDate };
